/**
 * Замок минулих днів і замки окремих днів.
 *
 * Настав новий день — учорашній закрився. Так випадковий клік або протяжка не
 * переписують те, за чим уже подали рапорт чи здали список ПЗ. Замок знімається
 * кнопкою в панелі й лишається знятим до перезавантаження сторінки: це разова
 * дія «зараз правлю минуле», а не постійний режим.
 *
 * Кожен день можна ще й закрити чи відкрити окремо — замком у шапці дня. Такий
 * вибір перебиває загальний замок: закритий вручну день лишається закритим і
 * після зняття загального замка, а відкритий вручну минулий день правиться,
 * хоч загальний замок стоїть. Живе так само до перезавантаження сторінки.
 */

import { todayIso, type IsoDate } from '@kancelaria/shared';
import { useMemo } from 'react';
import { create } from 'zustand';

interface LockState {
  /** Замок знято — минулі дні редагуються. */
  open: boolean;
  setOpen: (open: boolean) => void;
  /** Дні, закриті (`true`) чи відкриті (`false`) окремо від загального замка. */
  days: Readonly<Record<IsoDate, boolean>>;
  toggleDay: (date: IsoDate) => void;
}

export const useLock = create<LockState>((set, get) => ({
  open: false,
  setOpen: open => set({ open }),
  days: {},
  toggleDay: date => {
    const { open, days } = get();
    const byDefault = !open && date < todayIso();
    const locked = !(days[date] ?? byDefault);
    const next = { ...days };
    // Збіглося із загальним замком — окремий запис уже нічого не каже.
    if (locked === byDefault) delete next[date];
    else next[date] = locked;
    set({ days: next });
  },
}));

/**
 * Перевірка «чи закритий день» під поточний стан замків.
 *
 * Збираємо раз на пачку, а не питаємо стан для кожної клітинки: через `apply`
 * проходять пачки на десятки тисяч клітинок, і рахувати «сьогодні» для кожної
 * з них — марна робота.
 */
export function lockCheck(
  state: Pick<LockState, 'open' | 'days'> = useLock.getState(),
): (date: IsoDate) => boolean {
  const edge = state.open ? '' : todayIso();
  const days = state.days;
  return date => days[date] ?? (!!edge && date < edge);
}

/** Те саме як хук: компонент перемальовується, коли змінився будь-який замок. */
export function useLockCheck(): (date: IsoDate) => boolean {
  const open = useLock(s => s.open);
  const days = useLock(s => s.days);
  // Сталий, поки не змінився замок чи дата: на ньому тримаються `useMemo` таблиці.
  const today = todayIso();
  return useMemo(() => lockCheck({ open, days }), [open, days, today]);
}
